import type { AccountUserSettingDto } from '../../types/api';

/**
 * Per-SRA idle sign-out timeout. Stored as a ConfigInfo entry on the SRA record and mirrored into
 * localStorage (same key) for the web idle sign-out hook (packages/web/src/hooks/useAutoSignOut.ts).
 */
export const SRA_TIMEOUT_SETTING = 'auto_signout_timeout';

export type SraTimeoutValue = '15min' | '30min' | '45min' | '1hour' | '2hours' | '3hours' | 'never';

/** Applied when an SRA has no timeout stored yet. */
export const DEFAULT_SRA_TIMEOUT: SraTimeoutValue = '1hour';

export const SRA_TIMEOUT_OPTIONS: ReadonlyArray<{ value: SraTimeoutValue; label: string; minutes: number | null }> = [
  { value: '15min', label: '15 min', minutes: 15 },
  { value: '30min', label: '30 min', minutes: 30 },
  { value: '45min', label: '45 min', minutes: 45 },
  { value: '1hour', label: '1 hour', minutes: 60 },
  { value: '2hours', label: '2 hours', minutes: 120 },
  { value: '3hours', label: '3 hours', minutes: 180 },
  { value: 'never', label: 'Never', minutes: null },
];

/** Maps stored/legacy values to a current option (legacy: 'disabled' => never, '4hours' => 3 hours). */
export const normalizeSraTimeout = (value: string | null | undefined): SraTimeoutValue => {
  const normalized = (value || '').trim().toLowerCase();
  if (normalized === 'disabled') return 'never';
  if (normalized === '4hours') return '3hours';
  const match = SRA_TIMEOUT_OPTIONS.find((option) => option.value === normalized);
  return match ? match.value : DEFAULT_SRA_TIMEOUT;
};

export const readSraTimeout = (configInfo: AccountUserSettingDto[] | undefined): SraTimeoutValue => {
  const entry = (configInfo ?? []).find((setting) => (setting.Name || '').trim().toLowerCase() === SRA_TIMEOUT_SETTING);
  return normalizeSraTimeout(entry?.Value);
};

/**
 * Mirrors the signed-in SRA's timeout into localStorage for the web idle sign-out hook and tells it
 * to re-arm. No-op off web, or when the stored value already matches.
 */
export const applySraTimeoutToLocalStorage = (configInfo: AccountUserSettingDto[] | undefined): void => {
  try {
    if (typeof localStorage === 'undefined' || typeof window === 'undefined') {
      return;
    }
    const next = readSraTimeout(configInfo);
    if (localStorage.getItem(SRA_TIMEOUT_SETTING) === next) {
      return;
    }
    localStorage.setItem(SRA_TIMEOUT_SETTING, next);
    window.dispatchEvent(new Event('autoSignOutSettingChanged'));
  } catch {
    // storage unavailable — idle sign-out falls back to its default
  }
};

export const writeSraTimeout = (
  configInfo: AccountUserSettingDto[],
  timeout: SraTimeoutValue
): AccountUserSettingDto[] => {
  const index = configInfo.findIndex((setting) => (setting.Name || '').trim().toLowerCase() === SRA_TIMEOUT_SETTING);
  if (index < 0) {
    return [...configInfo, { Name: SRA_TIMEOUT_SETTING, Value: timeout }];
  }
  return configInfo.map((setting, i) => (i === index ? { ...setting, Value: timeout } : setting));
};
